import React, { useState, useEffect } from 'react';
import { Configuration, CreateConfigurationRequest, UpdateConfigurationRequest, ConfigurationCategoryType, CONFIGURATION_CATEGORIES } from '../types';

interface ConfigurationFormProps {
  configuration?: Configuration;
  onSave: (data: CreateConfigurationRequest | UpdateConfigurationRequest) => Promise<void>;
  onCancel: () => void;
  isLoading?: boolean;
}

const ConfigurationForm: React.FC<ConfigurationFormProps> = ({
  configuration,
  onSave,
  onCancel,
  isLoading = false
}) => {
  const [formData, setFormData] = useState({
    category: '',
    key: '',
    value: '',
    label: '',
    description: '',
    isActive: true,
    sortOrder: 0,
    metadata: {}
  });

  const [errors, setErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    if (configuration) {
      setFormData({
        category: configuration.category,
        key: configuration.key,
        value: typeof configuration.value === 'string' ? configuration.value : JSON.stringify(configuration.value),
        label: configuration.label,
        description: configuration.description,
        isActive: configuration.isActive,
        sortOrder: configuration.sortOrder,
        metadata: configuration.metadata
      });
    }
  }, [configuration]);

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.category) {
      newErrors.category = 'Category is required';
    }
    if (!formData.key) {
      newErrors.key = 'Key is required';
    }
    if (!formData.value) {
      newErrors.value = 'Value is required';
    }
    if (!formData.label) {
      newErrors.label = 'Label is required';
    }
    if (formData.sortOrder < 0) {
      newErrors.sortOrder = 'Sort order must be a positive number';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    try {
      let processedValue = formData.value;
      
      // Try to parse JSON if it looks like JSON
      if (formData.value.startsWith('{') || formData.value.startsWith('[')) {
        try {
          processedValue = JSON.parse(formData.value);
        } catch {
          // Keep as string if JSON parsing fails
        }
      }

      const submitData = {
        ...formData,
        value: processedValue,
        sortOrder: Number(formData.sortOrder)
      };

      // Remove category and key for updates
      if (configuration) {
        const { category, key, ...updateData } = submitData;
        await onSave(updateData);
      } else {
        await onSave(submitData);
      }
    } catch (error) {
      console.error('Error processing form data:', error);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? (e.target as HTMLInputElement).checked : 
              type === 'number' ? Number(value) : value
    }));

    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  return (
    <form onSubmit={handleSubmit} className="configuration-form">
      <div className="form-row">
        {/* Category */}
        <div className="form-group">
          <label htmlFor="category">
            Category *
          </label>
          <select
            id="category"
            name="category"
            value={formData.category}
            onChange={handleInputChange}
            disabled={!!configuration} // Disable for updates
            className={errors.category ? 'error' : ''}
          >
            <option value="">Select Category</option>
            {CONFIGURATION_CATEGORIES.map(category => (
              <option key={category} value={category}>
                {category.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
              </option>
            ))}
          </select>
          {errors.category && (
            <p className="error-message">{errors.category}</p>
          )}
        </div>

        {/* Key */}
        <div className="form-group">
          <label htmlFor="key">
            Key *
          </label>
          <input
            type="text"
            id="key"
            name="key"
            value={formData.key}
            onChange={handleInputChange}
            disabled={!!configuration} // Disable for updates
            className={errors.key ? 'error' : ''}
            placeholder="Enter configuration key"
          />
          {errors.key && (
            <p className="error-message">{errors.key}</p>
          )}
        </div>
      </div>

      <div className="form-row">
        {/* Label */}
        <div className="form-group">
          <label htmlFor="label">
            Label *
          </label>
          <input
            type="text"
            id="label"
            name="label"
            value={formData.label}
            onChange={handleInputChange}
            className={errors.label ? 'error' : ''}
            placeholder="Enter display label"
          />
          {errors.label && (
            <p className="error-message">{errors.label}</p>
          )}
        </div>

        {/* Sort Order */}
        <div className="form-group">
          <label htmlFor="sortOrder">
            Sort Order
          </label>
          <input
            type="number"
            id="sortOrder"
            name="sortOrder"
            value={formData.sortOrder}
            onChange={handleInputChange}
            min="0"
            className={errors.sortOrder ? 'error' : ''}
            placeholder="0"
          />
          {errors.sortOrder && (
            <p className="error-message">{errors.sortOrder}</p>
          )}
        </div>
      </div>

      {/* Value */}
      <div className="form-group">
        <label htmlFor="value">
          Value *
        </label>
        <textarea
          id="value"
          name="value"
          value={formData.value}
          onChange={handleInputChange}
          rows={3}
          className={errors.value ? 'error' : ''}
          placeholder="Enter configuration value (can be JSON for complex data)"
        />
        {errors.value && (
          <p className="error-message">{errors.value}</p>
        )}
        <p className="setting-description">
          For simple values, enter text. For complex data, use JSON format.
        </p>
      </div>

      {/* Description */}
      <div className="form-group">
        <label htmlFor="description">
          Description
        </label>
        <textarea
          id="description"
          name="description"
          value={formData.description}
          onChange={handleInputChange}
          rows={2}
          placeholder="Enter description (optional)"
        />
      </div>

      {/* Active Status */}
      <div className="form-group">
        <label className="checkbox-label">
          <input
            type="checkbox"
            id="isActive"
            name="isActive"
            checked={formData.isActive}
            onChange={handleInputChange}
          />
          <span>Active</span>
        </label>
      </div>

      {/* Form Actions */}
      <div className="form-actions">
        <button
          type="button"
          onClick={onCancel}
          className="btn-primary btn-secondary"
        >
          Cancel
        </button>
        <button
          type="submit"
          disabled={isLoading}
          className="btn-primary btn-success"
        >
          {isLoading ? 'Saving...' : configuration ? 'Update' : 'Create'}
        </button>
      </div>
    </form>
  );
};

export default ConfigurationForm;
